<template>
  <div class="flex flex-col min-h-0 page-view">
    <BasePageHeader :title="$t('nav.transfers')">
      <template #actions>
        <button class="btn primary small" @click="router.push('/transfers/new')">{{ $t('common.new') }}</button>
      </template>
    </BasePageHeader>

    <!-- Filter bar -->
    <div class="sticky z-30" style="top:var(--app-header-offset, 3.5rem); background:rgba(11,15,17,0.92); backdrop-filter:blur(8px); border-bottom:1px solid var(--color-border);">
      <!-- Search row -->
      <div class="px-4 py-3 flex items-center gap-3">
        <div class="flex-1">
          <SearchField
            v-model="filter"
            :debounce="0"
            :placeholder="$t('transfers.filterPlaceholder')"
          />
        </div>
      </div>
      <!-- Status chips -->
      <div class="flex gap-1.5 px-4 pb-2.5 overflow-x-auto [scrollbar-width:none] [-webkit-overflow-scrolling:touch]">
        <button
          v-for="chip in STATUS_CHIPS"
          :key="String(chip.value)"
          @click="statusFilter = chip.value"
          class="flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium border cursor-pointer transition-all"
          :style="statusFilter === chip.value ? chip.activeStyle : 'background:#1f2937; color:#9ca3af; border-color:#374151;'"
        >{{ chip.label }}</button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="px-4 pt-4 space-y-3">
      <div v-for="i in 6" :key="i" class="h-16 rounded-xl bg-white/5 animate-pulse" />
    </div>

    <!-- Error -->
    <div v-else-if="loadError" class="flex flex-col items-center justify-center py-20 text-center px-8">
      <p class="text-red-400 font-medium">{{ loadError }}</p>
    </div>

    <!-- Empty -->
    <div v-else-if="!filtered.length" class="flex flex-col items-center justify-center py-20 text-center px-8">
      <p class="text-gray-300 font-medium">{{ filter ? $t('transfers.noMatchingTransfers') : $t('transfers.noTransfersYet') }}</p>
      <p class="text-gray-500 text-sm mt-1">{{ filter ? $t('transfers.tryDifferentSearch') : $t('transfers.createFirst') }}</p>
    </div>

    <!-- List -->
    <ul v-else class="px-4 py-3 space-y-2">
      <li
        v-for="t in filtered"
        :key="t.id"
        @click="router.push(`/transfers/${t.id}`)"
        class="flex items-center gap-3 p-3 rounded-xl cursor-pointer active:scale-[0.98] transition-transform"
        style="background:#0f1720; border:1px solid rgba(255,255,255,0.06);"
      >
        <!-- Status indicator -->
        <div
          class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold"
          :style="statusStyle(t.status)"
        >
          {{ statusInitial(t.status) }}
        </div>

        <div class="flex-1 min-w-0">
          <div class="text-sm font-semibold text-gray-100 truncate">{{ t.name }}</div>
          <div class="text-xs text-gray-300 truncate mt-0.5">
            <template v-if="warehouseName(t.destination_warehouse)">
              <span>{{ warehouseName(t.destination_warehouse) }}</span>
              <span class="text-gray-500" v-if="warehouseLocation(t.destination_warehouse)"> — {{ warehouseLocation(t.destination_warehouse) }}</span>
              <span v-if="areaName(t.destination_area)" class="text-gray-500"> / {{ areaName(t.destination_area) }}</span>
            </template>
            <span v-else class="text-gray-500">{{ $t('transfers.noDestination') }}</span>
          </div>
          <div class="flex items-center gap-2 mt-1 flex-wrap">
            <span class="text-xs font-medium px-1.5 py-0.5 rounded" :style="statusBadgeStyle(t.status)">
              {{ transferStatusLabel(t.status, t.status_display) }}
            </span>
            <span v-if="t.line_count != null" class="text-xs text-gray-500">
              {{ t.received_line_count ?? 0 }}/{{ t.line_count }} {{ $t('ui.items') }}
            </span>
            <span v-if="t.dispatched_at" class="text-xs text-gray-600">· {{ formatDate(t.dispatched_at) }}</span>
            <span v-else-if="t.created_at" class="text-xs text-gray-600">· {{ formatDate(t.created_at) }}</span>
          </div>
        </div>

        <svg class="shrink-0 text-gray-600" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
      </li>
    </ul>

  </div>
</template>

<script setup>
import { useStatusLabels } from '@/composables/useStatusLabels.js'
const { transferStatusLabel } = useStatusLabels()

import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { getTransfers, getWarehouses, getAreas, getLocations } from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import SearchField from '@/components/search/SearchField.vue'

const router = useRouter()
const { t } = useI18n()
const filter = ref('')
const statusFilter = ref(null)
const transfers = ref([])
const warehouses = ref([])
const areas = ref([])

const STATUS_CHIPS = computed(() => [
  { value: null, label: t('common.all'),           activeStyle: 'background:#2563eb; color:#fff; border-color:#1d4ed8;' },
  { value: 0,    label: t('transfers.status.draft'),       activeStyle: 'background:#1c2a3a; color:#93c5fd; border-color:#3b82f6;' },
  { value: 1,    label: t('transfers.status.dispatched'),  activeStyle: 'background:#1c2a20; color:#86efac; border-color:#22c55e;' },
  { value: 2,    label: t('transfers.status.partial'),     activeStyle: 'background:#2a2a1c; color:#fde68a; border-color:#eab308;' },
  { value: 3,    label: t('transfers.status.received'),    activeStyle: 'background:#1a2a1a; color:#4ade80; border-color:#16a34a;' },
  { value: 4,    label: t('transfers.status.cancelled'),   activeStyle: 'background:#2a1c1c; color:#f87171; border-color:#ef4444;' },
])
const locations = ref([])
const loading = ref(false)
const loadError = ref(null)

const STATUSES = { 0: t('transfers.status.draft'), 1: t('transfers.status.dispatched'), 2: t('ui.partiallyReceived'), 3: t('transfers.status.received'), 4: t('transfers.status.cancelled') }

function warehouseName(id) {
  return warehouses.value.find(w => w.id === id)?.name ?? null
}

function warehouseLocation(warehouseId) {
  const w = warehouses.value.find(w => w.id === warehouseId)
  if (!w) return null
  return locations.value.find(l => l.id === w.location)?.name ?? null
}

function areaName(id) {
  return areas.value.find(a => a.id === id)?.name ?? null
}

const filtered = computed(() => {
  let list = statusFilter.value !== null
    ? transfers.value.filter(t => t.status === statusFilter.value)
    : transfers.value
  const q = filter.value.trim().toLowerCase()
  if (!q) return list
  return list.filter(t =>
    t.name?.toLowerCase().includes(q) ||
    transferStatusLabel(t.status, t.status_display).toLowerCase().includes(q) ||
    warehouseName(t.destination_warehouse)?.toLowerCase().includes(q) ||
    areaName(t.destination_area)?.toLowerCase().includes(q)
  )
})

function statusStyle(status) {
  const map = {
    0: 'background:#1c2a3a; color:#93c5fd;',
    1: 'background:#1c2a20; color:#86efac;',
    2: 'background:#2a2a1c; color:#fde68a;',
    3: 'background:#1a2a1a; color:#4ade80;',
    4: 'background:#2a1c1c; color:#f87171;',
  }
  return map[status] ?? 'background:#1f2937; color:#9ca3af;'
}

function statusBadgeStyle(status) {
  const map = {
    0: 'background:#1c2a3a; color:#93c5fd;',
    1: 'background:#1c2a20; color:#86efac;',
    2: 'background:#2a2a1c; color:#fde68a;',
    3: 'background:#1a2a1a; color:#4ade80;',
    4: 'background:#2a1c1c; color:#f87171;',
  }
  return map[status] ?? 'background:#1f2937; color:#9ca3af;'
}

function statusInitial(status) {
  return { 0: 'D', 1: 'DP', 2: 'PR', 3: 'R', 4: 'X' }[status] ?? '?'
}

function formatDate(dt) {
  if (!dt) return ''
  return new Date(dt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

async function load() {
  loading.value = true
  loadError.value = null
  try {
    const [res, wRes, aRes, lRes] = await Promise.all([getTransfers(), getWarehouses(), getAreas(), getLocations()])
    transfers.value = (res.results ?? res ?? []).reverse()
    warehouses.value = wRes.results ?? wRes ?? []
    areas.value = aRes.results ?? aRes ?? []
    locations.value = lRes.results ?? lRes ?? []
  } catch {
    loadError.value = t('transfers.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>
